import React, { Dispatch, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    ClickAwayListener,
    Drawer,
    DrawerProps,
    Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fullSize } from '../../../Constants';
import { IAppReduxStore } from '../../../redux/store';
import ImportTable from '../../General/ImportTable/ImportTable';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../General/Toast/Toast';
import ImportViewSidebar from './ImportViewSidebar';
import SelectedDocumentsIndicator from '../ProjectView/SelectedDocumentsIndicator';
import DocnumberSidepanel from '../../General/Sidepanels/DocnumberSidepanel';
import { toggleDocnumberSidepanel } from '../../../redux/import/sliceDocnumberSidepanel';
import {
    resetNumberOfDownloadedDocuments,
    setShowSingleDocumentLoading,
} from '../../../redux/project/file/sliceProjectFileDownload';
import { fetchImportDocuments } from '../../../util/ImportUtils';
import { ProjectSidepanel, setActiveSidepanel } from '../../../redux/project/sliceProjectSidepanel';
import AppSidepanel from '../../General/Sidepanels/AppSidepanel';
import { resetErrorPostProjectAccessLog } from '../../../redux/general/sliceDatabaseAccess';
import { resetErrorDynamicAction } from '../../../redux/project/documents/sliceProjectDocumentsDynamicSelection';
import { resetCreateNoticeErrorState } from '../../../redux/project/documents/sliceProjectDocumentsCreateNotice';
import {
    closeSaveAssemblyDialog,
    resetErrorSaveAssembly,
    resetSaveAssembly,
    resetSelectedDocument,
} from '../../../redux/project/documents/sliceProjectAssembly';
import { resetRedliningReportState } from '../../../redux/project/documents/sliceProjectReportTemplate';
import { resetCategorizationState } from '../../../redux/import/sliceImportDocuments';
import { isICategorizationSuccess } from '../../../redux/general/sliceCategorization';
import { resetGetProjectUsersErrorState } from '../../../redux/project/sliceProjectUserList';
import { clearAdditionalDocuments } from '../../../redux/project/documents/sliceProjectAdditionalDocuments';
import { resetChangeReleaseStatusErrorState } from '../../../redux/project/documents/sliceProjectAdditionalDocumentChangeReleaseStatus';
import { useFilesOpen } from '../../../hooks/useFilesOpen';
import { useAddFiles } from '../../../hooks/useAddFiles';
import {
    resetVdcViewerUpload,
    resetLoadingVdcViewerUpload,
    resetErrorVdcViewerUpload,
} from '../../../redux/project/documents/sliceProjectVdcCollaboration';
import {
    resetIsSignatureSaved,
    resetUserSignatureError,
} from '../../../redux/project/sliceProjectUserSignatures';

/** Props for the Sidepanel Drawer. */
const drawerProps: DrawerProps = {
    PaperProps: { style: { position: 'absolute' }, elevation: 3 },
    variant: 'permanent',
    anchor: 'right',
};

/**Create a new instance of BroadcastChannel to communicate between different documents */
const bc = new BroadcastChannel('project_view');

/**
 * Component that shows the content of the ProjectView.
 */
export default () => {
    const { replicaId } = useParams();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });
    const componentRef = useRef<HTMLDivElement>();

    /** Open Files hook with error handling */
    useFilesOpen();

    /** Add Files hook with error handling */
    useAddFiles();

    /** Access relevant state variables from the redux store. */
    const numberOfDownloadedDocuments = useSelector(
        (_store: IAppReduxStore) => _store.projectFileDownload.numberOfDownloadedDocuments,
    );
    const showSingleDocumentLoading = useSelector(
        (_store: IAppReduxStore) => _store.projectFileDownload.showSingleDocumentLoading,
    );
    const errorDownload = useSelector(
        (_store: IAppReduxStore) => _store.projectFileDownload.downloadDocuments.error,
    );
    const errorDownloadZip = useSelector(
        (_store: IAppReduxStore) => _store.projectFileDownload.downloadZip.error,
    );
    const errorExportToCSV = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsExportToCSV.error,
    );
    const { error: errorDatabaseAccess } = useSelector(
        (store: IAppReduxStore) => store.databaseAccess,
    );
    const { error: errorDynamicAction } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsDynamicSelection,
    );
    const { error: errorCreateNotice } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsCreateNotice,
    );
    const { error: errorProjectUserList } = useSelector(
        (store: IAppReduxStore) => store.projectUserList,
    );
    const areasLoading = useSelector((_store: IAppReduxStore) => _store.projectAreas.loading);
    const viewsLoading = useSelector((_store: IAppReduxStore) => _store.projectViews.loading);
    const viewStructureLoading = useSelector(
        (_store: IAppReduxStore) => _store.projectViewData.structure.loading,
    );
    const isSidepanelOpen = useSelector(
        (_store: IAppReduxStore) => _store.docnumberSidepanel.isSidepanelOpen,
    );

    const { redliningReportState } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsReportFromTemplate,
    );
    const openAppSidepanel = useSelector((store: IAppReduxStore) => store.projectSidepanel.active);
    const { categorizationState: importCategorizationState } = useSelector(
        (_store: IAppReduxStore) => _store.importDocuments,
    );
    const { error: errorSaveAssembly, data: dataSaveAssembly } = useSelector(
        (store: IAppReduxStore) => store.projectSaveAssembly,
    );
    const { error: errorChangeAdditionalDocumentReleaseStatus } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentChangeReleaseStatus,
    );
    const {
        error: errorVdcDocumentUpload,
        data: successVdcDocumentUpload,
        loading: loadingVdcDocumentUpload,
    } = useSelector((store: IAppReduxStore) => store.projectVdcCollaboration.vdcViewerUploadState);

    const { error: errorSavingUserSignature, isSignatureSaved: isUserSignatureSaved } = useSelector(
        (store: IAppReduxStore) => store.projectUserSignatureSettings,
    );

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    useEffect(() => {
        if (isICategorizationSuccess(importCategorizationState)) {
            enqueueSnackbar(
                t(`categorizationSuccess`, {
                    count: importCategorizationState.docCount,
                }),
                {
                    severity: ToastSeverity.Success,
                },
            );
            dispatch(resetCategorizationState());
        }
    }, [importCategorizationState]);

    useEffect(() => {
        if (isUserSignatureSaved) {
            enqueueSnackbar(t(`userSignatureSuccess`), {
                severity: ToastSeverity.Success,
            });
            dispatch(resetIsSignatureSaved());
        }
    }, [isUserSignatureSaved]);

    const redliningReportStateCallbacks = {
        Pending: () => {
            enqueueSnackbar(t('toastProtocolExportDocumentInfo'));
        },
        Succeed: () => {
            enqueueSnackbar(t('toastAdvanceExportSuccess', { numberOfDocuments: 1 }), {
                severity: ToastSeverity.Success,
            });
        },
        Failed: () => {
            enqueueSnackbar(t('errorRedliningReport'), {
                severity: ToastSeverity.Error,
            });
        },
        NoRedlinings: () => {
            enqueueSnackbar(t('infoNoRedlinings'));
        },
    };

    useEffect(() => {
        const callback = redliningReportStateCallbacks[redliningReportState];

        if (callback) {
            callback();
            dispatch(resetRedliningReportState());
        }
    }, [redliningReportState]);

    /** Display open documents errors as the queue of Snackbars. */
    useEffect(() => {
        const onmessage = (ev: MessageEvent<string>) => {
            enqueueSnackbar(t('errorViewDocument'), {
                additionalInfo: ev.data,
                severity: ToastSeverity.Error,
            });
        };
        bc.addEventListener('message', onmessage);
        return () => bc.removeEventListener('message', onmessage);
    }, []);

    useEffect(() => {
        if (showSingleDocumentLoading) {
            enqueueSnackbar(t('toastDownloadInfo', { numberOfDocuments: 1 }), {
                severity: ToastSeverity.Info,
            });
            dispatch(setShowSingleDocumentLoading(false));
            dispatch(resetNumberOfDownloadedDocuments());
        }
    }, [showSingleDocumentLoading]);

    useEffect(() => {
        if (numberOfDownloadedDocuments) {
            enqueueSnackbar(
                t('toastDownloadSuccess', {
                    numberOfDocuments: numberOfDownloadedDocuments,
                }),
                {
                    severity: ToastSeverity.Success,
                },
            );
            dispatch(resetNumberOfDownloadedDocuments());
        }
    }, [numberOfDownloadedDocuments]);

    useEffect(() => {
        if (dataSaveAssembly) {
            enqueueSnackbar(t('toastUpdateAssemblySuccess'), {
                severity: ToastSeverity.Success,
            });

            fetchImportDocuments(replicaId);

            dispatch(resetSaveAssembly());
            dispatch(resetSelectedDocument());
            dispatch(closeSaveAssemblyDialog());

            dispatch(clearAdditionalDocuments());
        }
    }, [dataSaveAssembly]);

    useEffect(() => {
        const numberOfFailedDocuments = successVdcDocumentUpload?.failedDocuments?.length;

        if (numberOfFailedDocuments > 0) {
            enqueueSnackbar(
                t('toastVdcUploadWarning', {
                    numberOfDocuments: numberOfFailedDocuments,
                }),
                {
                    severity: ToastSeverity.Warning,
                },
            );

            resetVdcViewerUpload();
        } else if (successVdcDocumentUpload) {
            enqueueSnackbar(t('toastVdcUploadSuccess'), {
                severity: ToastSeverity.Success,
            });

            resetVdcViewerUpload();
        }
    }, [successVdcDocumentUpload]);

    useEffect(() => {
        if (loadingVdcDocumentUpload) {
            enqueueSnackbar(t('toastVdcUploadLoading'), {
                severity: ToastSeverity.Info,
            });

            resetLoadingVdcViewerUpload();
        }
    }, [loadingVdcDocumentUpload]);

    /** Display download errors as the queue of Snackbars. */
    useEffect(() => {
        errorDownload &&
            enqueueSnackbar(t('fileDownloadError'), {
                additionalInfo: errorDownload.message,
                severity: ToastSeverity.Error,
            });
        errorDownloadZip &&
            enqueueSnackbar(t('zipDownloadError'), {
                additionalInfo: errorDownloadZip.message,
                severity: ToastSeverity.Error,
            });
        errorExportToCSV &&
            enqueueSnackbar(t('exportToCSVError'), {
                additionalInfo: errorExportToCSV.message,
                severity: ToastSeverity.Error,
            });

        if (errorDatabaseAccess) {
            enqueueSnackbar(t('errorDatabaseAccess'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorPostProjectAccessLog());
        }
        if (errorCreateNotice) {
            enqueueSnackbar(t('errorCreateNotice'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetCreateNoticeErrorState());
        }
        if (errorProjectUserList) {
            enqueueSnackbar(t('errorProjectUserList'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetGetProjectUsersErrorState());
        }
        if (errorSaveAssembly) {
            enqueueSnackbar(t('toastUpdateAssemblyError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorSaveAssembly());
        }
        if (errorDynamicAction) {
            enqueueSnackbar(t('errorDynamicAction'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorDynamicAction());
        }
        if (errorChangeAdditionalDocumentReleaseStatus) {
            enqueueSnackbar(t('errorChangeAdditionalDocumentReleaseStatus'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetChangeReleaseStatusErrorState());
        }
        if (errorVdcDocumentUpload) {
            enqueueSnackbar(t('toastVdcUploadError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetErrorVdcViewerUpload());
        }
        if (errorSavingUserSignature) {
            enqueueSnackbar(t('errorSavingUserSignature'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetUserSignatureError());
        }
    }, [
        errorDownload,
        errorDownloadZip,
        errorExportToCSV,
        errorDatabaseAccess,
        errorCreateNotice,
        errorProjectUserList,
        errorSaveAssembly,
        errorDynamicAction,
        errorChangeAdditionalDocumentReleaseStatus,
        errorVdcDocumentUpload,
        errorSavingUserSignature,
    ]);

    useEffect(() => {
        if (!isSidepanelOpen) return;
        componentRef.current.click();
    }, [isSidepanelOpen]);

    useEffect(() => {
        if (openAppSidepanel === ProjectSidepanel.None) return;
        componentRef.current.click();
    }, [openAppSidepanel]);

    /** Waiting on a request, so show a loading indicator. */
    if (areasLoading || viewsLoading || viewStructureLoading) {
        return (
            <Stack
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...fullSize,
                }}
            >
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Stack
            direction='row'
            sx={{
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <ImportViewSidebar />
            <Stack
                direction='row'
                sx={{ ...fullSize, pl: 2, position: 'relative', overflow: 'hidden' }}
            >
                <Stack
                    sx={{
                        overflow: 'auto',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <ImportTable />
                    <SelectedDocumentsIndicator />
                </Stack>
                <ClickAwayListener
                    mouseEvent='onMouseDown'
                    touchEvent='onTouchStart'
                    onClickAway={() => {
                        isSidepanelOpen && dispatch(toggleDocnumberSidepanel(false));
                        openAppSidepanel !== ProjectSidepanel.None &&
                            dispatch(setActiveSidepanel(ProjectSidepanel.None));
                    }}
                >
                    <Drawer
                        {...drawerProps}
                        open={isSidepanelOpen || openAppSidepanel !== ProjectSidepanel.None}
                    >
                        <Box sx={{ height: '100%' }} ref={componentRef}>
                            {isSidepanelOpen && (
                                <DocnumberSidepanel
                                    onSaved={() => fetchImportDocuments(replicaId)}
                                />
                            )}
                            {openAppSidepanel !== ProjectSidepanel.None && <AppSidepanel />}
                        </Box>
                    </Drawer>
                </ClickAwayListener>
            </Stack>
        </Stack>
    );
};
